/**
 * Upward infinite scroll on the `before` cursor — and the scroll anchoring without
 * which it is a bug rather than a feature.
 *
 * The obvious alternative is a "Load Previous Chapters" button. Replacing it with a sentinel is
 * the easy half. The
 * half that decides whether this feels finished is what happens *after* the page arrives:
 * inserting six chapters above the reader pushes everything they were looking at down by the
 * height of those six chapters, and a feed that throws the reader into the middle of chapter
 * 14 every time they scroll up is worse than the button. So the position is measured before
 * the request and restored in a layout effect after the render — before the browser paints,
 * so there is no visible jump to correct.
 *
 * `overflow-anchor` does this natively in Chrome and Firefox and does not exist in Safari,
 * which is half our mobile traffic. This is four lines and works everywhere.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { anchoredScrollTop } from '@/features/story/scroll';

export interface OlderChapters {
  /** `hasNextPage` — false once the page containing chapter 1 has been loaded. */
  has: boolean;
  loading: boolean;
  load: () => void;
}

/**
 * Start the request while the sentinel is still this far below the viewport's top edge. A page
 * takes a moment to arrive; loading only once it is on screen means the reader watches a
 * loading line instead of reading.
 */
const PREFETCH_MARGIN_PX = 800;

/**
 * @param oldest the lowest chapter number currently rendered. Its *change* is the signal that
 * a page arrived and the anchor is due — a render count or a length would also fire on the
 * newest chapter committing at the other end of the feed, which must not move anything.
 */
export function useOlderChapters(
  older: OlderChapters | undefined,
  oldest: number | undefined,
): React.RefObject<HTMLDivElement | null> {
  const sentinel = useRef<HTMLDivElement | null>(null);
  const anchor = useRef<{ scrollTop: number; content: number } | null>(null);

  const has = older?.has ?? false;
  const loading = older?.loading ?? false;
  const load = older?.load;

  useEffect(() => {
    const node = sentinel.current;
    // No sentinel is rendered when there is nothing older, and `IntersectionObserver` is
    // absent in jsdom — in both cases there is simply no auto-loading, which is a feed that
    // shows what it has rather than a crash.
    if (!node || !has || loading || !load || typeof IntersectionObserver !== 'function') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        anchor.current = {
          scrollTop: window.scrollY,
          content: document.documentElement.scrollHeight,
        };
        load();
      },
      { rootMargin: `${String(PREFETCH_MARGIN_PX)}px 0px 0px 0px` },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [has, loading, load]);

  useLayoutEffect(() => {
    const before = anchor.current;
    if (before === null) return;
    anchor.current = null;
    window.scrollTo({
      top: anchoredScrollTop(before, document.documentElement.scrollHeight),
      behavior: 'auto',
    });
  }, [oldest]);

  return sentinel;
}
